import { useState } from 'react';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { HeartIcon as HeartIconOutline } from '@heroicons/react/24/outline';
import { ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { formatDate } from '../pages/utils/format';

export default function ReviewCard({ review, onProductClick }) {
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(review.likeCount || 0);
  const [showReplies, setShowReplies] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  const handleLike = async () => {
    try {
      const { data } = await api.post(`/reviews/${review._id}/like`);
      setIsLiked(data.data.isLiked);
      setLikeCount(data.data.likeCount);
    } catch (err) {
      console.error('Error liking review:', err);
    }
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim()) return;
    
    try {
      setIsSubmittingReply(true);
      await api.post(`/reviews/${review._id}/reply`, { comment: replyText });
      setReplyText('');
      // You might want to refresh the review data here
    } catch (err) {
      console.error('Error submitting reply:', err);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  return (
    <div className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
      <div className="flex items-start space-x-4">
        <img
          src={review.user?.avatar || '/default-avatar.png'}
          alt={review.user?.firstName}
          className="h-10 w-10 rounded-full object-cover"
        />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">
                {review.user?.firstName} {review.user?.lastName}
              </h4>
              <div className="flex items-center mt-1">
                {[...Array(5)].map((_, i) => (
                  i < review.rating ? (
                    <StarIconSolid key={i} className="h-4 w-4 text-yellow-400" />
                  ) : (
                    <StarIconOutline key={i} className="h-4 w-4 text-gray-300" />
                  )
                ))}
                <span className="ml-2 text-sm text-gray-500">
                  {formatDate(review.createdAt)}
                </span>
              </div>
            </div>
            {review.isVerifiedPurchase && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Verified Purchase
              </span>
            )}
          </div>

          {review.title && (
            <h5 className="mt-2 font-medium text-gray-900">{review.title}</h5>
          )}
          <p className="mt-1 text-gray-700">{review.comment}</p>

          {review.images?.length > 0 && (
            <div className="mt-3 flex space-x-2 overflow-x-auto pb-2">
              {review.images.map((image, idx) => (
                <img
                  key={idx}
                  src={image.url}
                  alt={`Review ${idx + 1}`}
                  className="h-24 w-24 rounded-md object-cover border border-gray-200"
                />
              ))}
            </div>
          )}

          {review.product && (
            <div 
              className="mt-3 flex items-center text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
              onClick={onProductClick}
            >
              <span>Product: {review.product.name}</span>
            </div>
          )}

          <div className="mt-3 flex items-center space-x-4">
            <button 
              onClick={handleLike}
              className="flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              {isLiked ? (
                <HeartIconSolid className="h-5 w-5 text-red-500" />
              ) : (
                <HeartIconOutline className="h-5 w-5" />
              )}
              <span className="ml-1">{likeCount}</span>
            </button>
            {review.replies?.length > 0 && (
              <button 
                onClick={() => setShowReplies(!showReplies)}
                className="flex items-center text-sm text-gray-500 hover:text-gray-700"
              >
                <ChatBubbleLeftIcon className="h-5 w-5" />
                <span className="ml-1">{review.replies.length} replies</span>
              </button>
            )}
          </div>

          {showReplies && review.replies?.length > 0 && (
            <div className="mt-4 pl-4 border-l-2 border-gray-200 space-y-4">
              {review.replies.map((reply, idx) => (
                <div key={idx} className="pt-2 first:pt-0">
                  <div className="flex items-start space-x-3">
                    <img
                      src={reply.user?.avatar || '/default-avatar.png'}
                      alt="Reply user"
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <div>
                      <h5 className="text-sm font-medium text-gray-900">
                        {reply.user?.firstName} {reply.user?.lastName}
                      </h5>
                      <p className="text-sm text-gray-700 mt-1">{reply.comment}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(reply.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}